import { useCallback, useEffect, useRef } from 'react';
import { identity } from 'lodash';
import { APP_EVENT_TYPE } from '../appEventType';
import {
    transformChangeEvent,
    transformClickEvent,
    transformHoverEvent,
    transformIntersectionEvent,
} from '../utils/UIEventTransformers';
import { parseUIEvent } from '../utils/parseUIEvent';

const eventConfig = [
    { type: APP_EVENT_TYPE.CLICK, DOMEventType: 'click', transformer: transformClickEvent },
    { type: APP_EVENT_TYPE.CHANGE, DOMEventType: 'change', transformer: transformChangeEvent },
    { type: APP_EVENT_TYPE.HOVER, DOMEventType: 'mouseenter', transformer: transformHoverEvent },
];

const getAdditionalData = event => {
    try {
        const additionalDataString = event.target.getAttribute('data-app-event-additional-data');
        return additionalDataString ? JSON.parse(additionalDataString) : {};
    } catch (error) {
        console.error('Error parsing additional data', error);
        return {};
    }
};

export const useAppEventUICollector = push => {
    const intersectionObserverRef = useRef(null);
    const mutationObserverRef = useRef(null);

    const listener = useCallback(
        (event, type, transformer = identity, additionalData = {}) => {
            const transformedEvent = transformer(parseUIEvent(event));
            if (transformedEvent) {
                transformedEvent.type = type;
                push({ ...transformedEvent, ...additionalData });
            }
        },
        [push]
    );

    const observeElement = element => {
        intersectionObserverRef.current?.observe(element);
    };

    const unobserveElement = element => {
        intersectionObserverRef.current?.unobserve(element);
    };

    const processElement = useCallback((element, processCallback) => {
        if (element.hasAttribute('data-app-event-visibility')) {
            processCallback(element);
        }

        element.querySelectorAll('[data-app-event-visibility]').forEach(processCallback);
    }, []);

    useEffect(
        function onDocumentLoad() {
            intersectionObserverRef.current = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const additionalData = getAdditionalData(entry);
                        listener(entry, APP_EVENT_TYPE.VISIBLE, transformIntersectionEvent, additionalData);
                    }
                });
            });

            processElement(document.body, observeElement);

            return () => {
                if (intersectionObserverRef.current) {
                    intersectionObserverRef.current.disconnect();
                }
            };
        },
        [listener, processElement]
    );

    useEffect(
        function onDocumentChange() {
            mutationObserverRef.current = new MutationObserver(mutations => {
                mutations.forEach(({ type, addedNodes, removedNodes, target, attributeName }) => {
                    if (type === 'childList') {
                        addedNodes.forEach(node => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                processElement(node, observeElement);
                            }
                        });
                        removedNodes.forEach(node => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                processElement(node, unobserveElement);
                            }
                        });
                    } else if (type === 'attributes') {
                        const attributeValue = target.getAttribute(attributeName);

                        if (attributeValue !== null) {
                            processElement(target, observeElement);
                        } else {
                            processElement(target, unobserveElement);
                        }
                    }
                });
            });

            mutationObserverRef.current.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['data-app-event-visibility'],
            });

            return () => {
                if (mutationObserverRef.current) {
                    mutationObserverRef.current.disconnect();
                }
            };
        },
        [listener, processElement]
    );

    useEffect(() => {
        const mappedConfig = eventConfig.map(({ DOMEventType, type, transformer }) => ({
            DOMEventType,
            listener: event => {
                const additionalData = getAdditionalData(event);
                listener(event, type, transformer, additionalData);
            },
        }));

        mappedConfig.forEach(({ DOMEventType, listener }) => {
            document.body.addEventListener(DOMEventType, listener, {
                capture: true,
                passive: true,
            });
        });

        const cleaner = () => {
            mappedConfig.forEach(({ DOMEventType, listener }) => {
                document.body.removeEventListener(DOMEventType, listener, {
                    capture: true,
                    passive: true,
                });
            });
        };

        return cleaner;
    }, [listener]);
};
